import { useState } from 'react';
import useWorkflowStore from '@/stores/WorkflowStore';
import { Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import { validateChannelCompatibility } from '@/lib/channelCapabilities';
import { validateWorkflow } from '@/lib/workflowUtils';
import { createWorkflow } from '@/hooks/workflow';
import { Button } from '@/components/ui/button';

import WorkflowSettingsDialog from '../WorkflowSettingsDialog';

const SaveWorkflow = ({ nodes, edges, id, className, disabled, ...props }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const navigate = useNavigate();

  // Get workflow store data
  const {
    workflowList,
    tempTrigger,
    workflowName,
    workflowDescription,
    channels,
    isActive,
    reEnrollment,
    enrollExisting,
    removeIfIneligible,
    setWorkflowList,
    setWorkflowName,
    setWorkflowDescription,
    setChannels,
    setIsActive,
    setReEnrollment,
    setEnrollExisting,
    setRemoveIfIneligible,
    setTempNodes,
    setTempEdges,
  } = useWorkflowStore(
    useShallow((state) => ({
      workflowList: state.workflowList,
      tempTrigger: state.tempTrigger,
      workflowName: state.workflowName,
      workflowDescription: state.workflowDescription,
      channels: state.channels,
      isActive: state.isActive,
      reEnrollment: state.reEnrollment,
      enrollExisting: state.enrollExisting,
      removeIfIneligible: state.removeIfIneligible,
      setWorkflowList: state.setWorkflowList,
      setWorkflowName: state.setWorkflowName,
      setWorkflowDescription: state.setWorkflowDescription,
      setChannels: state.setChannels,
      setIsActive: state.setIsActive,
      setReEnrollment: state.setReEnrollment,
      setEnrollExisting: state.setEnrollExisting,
      setRemoveIfIneligible: state.setRemoveIfIneligible,
      setTempNodes: state.setTempNodes,
      setTempEdges: state.setTempEdges,
    }))
  );

  // Convert the ReactFlow nodes and edges to the target format
  const convertToWorkflowFormat = () => {
    // Create a map to help track the order of nodes and their connections
    const nodeConnections = {};

    // Process edges to determine connections between nodes
    edges?.forEach((edge) => {
      if (edge.source && edge.target) {
        if (!nodeConnections[edge.source]) {
          nodeConnections[edge.source] = { next: edge.target };
        } else {
          nodeConnections[edge.source].next = edge.target;
        }
      }
    });

    // Create a map of node types for quick lookup
    const nodeTypesMap = {};
    nodes.forEach((node) => {
      nodeTypesMap[node.id] = node.type;
    });

    // Map branch data nodes to their branch IDs
    const branchDataMap = {};
    nodes
      .filter((node) => node.type === 'branch-data')
      .forEach((node) => {
        branchDataMap[node.data.branchId] = node.id;
      });

    // Steps array to return
    const steps = [];
    let currentOrder = 1;

    // Find the trigger node as starting point
    const triggerNode = nodes.find((node) => node.type === 'trigger');
    if (!triggerNode) return steps;

    // Use a queue for breadth-first traversal
    const processedNodes = new Set();

    // Start traversal from the trigger
    const traverseFromNode = (nodeId) => {
      if (processedNodes.has(nodeId)) return;
      processedNodes.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      // Skip trigger nodes in the steps output
      if (node.type === 'trigger') {
        // Continue with the node connected to the trigger
        const nextNodeId = nodeConnections[node.id]?.next;
        if (nextNodeId) {
          traverseFromNode(nextNodeId);
        }
        return;
      }

      // Process branch-config node
      if (node.type === 'branch-config') {
        const branchStep = {
          id: node.id,
          type: 'branch',
          order: currentOrder++,
          branches: [],
        };

        // Find the default branch
        const defaultBranch = node.data.branches.find(
          (b) => !b.conditions || b.conditions.length === 0
        );
        if (defaultBranch) {
          branchStep.default_branch_id = defaultBranch.id;
        }

        // Process each branch
        node.data.branches.forEach((branch) => {
          const branchData = {
            id: branch.id,
            name: branch.name,
          };

          // Get the branch data node
          const branchDataNodeId = branchDataMap[branch.id];

          if (branchDataNodeId) {
            // Find next node from branch data node
            const nextNodeId = nodeConnections[branchDataNodeId]?.next;
            if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
              branchData.next_step_id = nextNodeId;
            }
          }

          // Set default branch flag or add conditions
          if (branch.id === branchStep.default_branch_id) {
            branchData.default_branch = true;
          } else if (branch.conditions && branch.conditions.length > 0) {
            branchData.condition = convertConditions(branch.conditions);
          }

          branchStep.branches.push(branchData);
        });

        steps.push(branchStep);

        // Process each branch's connected nodes
        node.data.branches.forEach((branch) => {
          const branchDataNodeId = branchDataMap[branch.id];
          if (branchDataNodeId) {
            const nextNodeId = nodeConnections[branchDataNodeId]?.next;
            if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
              traverseFromNode(nextNodeId);
            }
          }
        });
      }
      // Process action node
      else if (node.type === 'action') {
        const actionType = node.data.actionType;
        const step = {
          id: node.id,
          type: actionType === 'delay' ? 'delay' : 'action',
          order: currentOrder++,
        };
        // Handle specific action types
        if (actionType === 'delay') {
          step.delay = node.data.config?.value || 0;
          step.delay_unit = node.data.config?.unit || 'seconds';
        } else if (actionType === 'send_message') {
          step.actions = [
            {
              type: 'send_message',
              value: node.data.config?.message || '',
            },
          ];
        } else if (actionType === 'reply_comment') {
          step.actions = [
            {
              type: 'reply_comment',
              value: node.data.config?.comment || '',
            },
          ];
        } else if (actionType === 'contact_add_tag') {
          step.actions = [
            {
              type: 'contact_add_tag',
              value: node.data.config?.tags || [],
            },
          ];
        } else if (actionType === 'contact_remove_tag') {
          step.actions = [
            {
              type: 'contact_remove_tag',
              value: node.data.config?.tags || [],
            },
          ];
        } else if (actionType === 'contact_change_owner') {
          if (node.data.config?.ownerMode === 'multiple') {
            step.actions = [
              {
                type: 'contact_change_owner',
                value: node.data.config?.owners || [],
                operator: 'weighted_allocation',
              },
            ];
          } else {
            step.actions = [
              {
                type: 'contact_change_owner',
                value: node.data.config?.owner || '',
              },
            ];
          }
        } else if (actionType === 'contact_state_change') {
          step.actions = [
            {
              type: 'contact_state_change',
              value: node.data.config?.state || '',
            },
          ];
        } else if (actionType === 'trigger_webhook') {
          step.actions = [
            {
              type: 'trigger_webhook',
              value: {
                url: node.data.config?.url || '',
                method: node.data.config?.method || 'POST',
              },
            },
          ];
        } else if (actionType === 'ai_select_message') {
          step.actions = [
            {
              type: 'ai_select_message',
              value: {
                messages: node.data.config?.messages || [],
                custom_prompt: node.data.config?.custom_prompt || '',
              },
            },
          ];
        } else if (actionType === 'linkedin_reply_comment') {
          step.actions = [
            {
              type: 'linkedin_reply_comment',
              value: node.data.config?.comment || '',
            },
          ];
        } else {
          // Handle other action types
          step.actions = [
            {
              type: actionType || 'unknown',
              value: node.data.config || {},
            },
          ];
        }

        // Add next step ID if exists and isn't an end node
        const nextNodeId = nodeConnections[node.id]?.next;
        if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
          step.next_step_id = nextNodeId;
        }

        steps.push(step);

        // Continue traversal with the next node
        if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
          traverseFromNode(nextNodeId);
        }
      }
    };

    // Start traversal from the trigger node
    traverseFromNode(triggerNode.id);

    return steps;
  };

  // Helper function to convert conditions
  const convertConditions = (conditions) => {
    if (!conditions || conditions.length === 0) {
      return undefined;
    }

    // For a single condition array
    if (conditions.length === 1 && conditions[0].length === 1) {
      const condition = conditions[0][0];
      return {
        type: 'condition',
        field: condition.field,
        operator: condition.operator,
        value: condition.value,
      };
    }

    // For multiple conditions (group)
    return {
      type: 'group',
      operator: 'AND', // Default to AND, adjust if needed
      conditions: conditions.flatMap((conditionGroup) =>
        conditionGroup.map((condition) => ({
          type: 'condition',
          field: condition.field,
          operator: condition.operator,
          value: condition.value,
        }))
      ),
    };
  };

  const handleSaveWorkflow = (settings) => {
    setIsSaving(true);

    try {
      // Convert workflow steps once
      const workflowSteps = convertToWorkflowFormat();

      // Validate with the generated steps using the shared validator
      const validationErrors = validateWorkflow(
        workflowSteps,
        tempTrigger,
        channels
      );

      if (validationErrors.length > 0) {
        // Display toast with validation errors
        validationErrors.forEach((error) => {
          toast.error(error);
        });
        setIsSaving(false);
        return;
      }

      // Validate channel compatibility
      const channelCompatibility = validateChannelCompatibility(
        tempTrigger,
        nodes,
        channels
      );

      if (!channelCompatibility.isValid) {
        // Display single toast with all channel compatibility issues
        toast.error(
          <div className='flex flex-col gap-1'>
            <span className='font-medium'>Channel compatibility issues:</span>
            {channelCompatibility.issues.map((issue, index) => {
              const channelList = issue.unsupportedChannels.join(', ');
              return (
                <span key={index} className='text-sm'>
                  • {issue.label} is not supported by: {channelList}
                </span>
              );
            })}
          </div>
        );
        setIsSaving(false);
        return;
      }

      // Create workflow object
      const workflow = {
        id: uuidv4(),
        name: workflowName || 'New Workflow',
        description: workflowDescription || '',
        channel: channels,
        is_active: isActive,
        re_enrollment: settings.re_enrollment,
        enroll_existing: settings.enroll_existing,
        remove_if_ineligible: settings.remove_if_ineligible,
        triggers: tempTrigger,
        steps: workflowSteps,
        createdAt: new Date().toISOString(),
      };

      // Use createWorkflow with toast promise
      toast.promise(createWorkflow(workflow), {
        loading: 'Saving workflow...',
        success: (response) => {
          // Update workflow store
          setWorkflowList({
            ...workflowList,
            [workflow.id]: workflow,
          });

          // Reset workflow state
          setWorkflowName('');
          setWorkflowDescription('');
          setChannels([]);
          setIsActive(true);
          setReEnrollment(false);
          setEnrollExisting(false);
          setRemoveIfIneligible(false);
          setTempNodes([]);
          setTempEdges([]);

          setIsSaving(false);
          navigate('/workflows');
          return 'Workflow saved successfully!';
        },
        error: (error) => {
          console.error('Error saving workflow:', error);
          setIsSaving(false);
          return error.response?.data?.message || 'Failed to save workflow';
        },
      });
    } catch (error) {
      console.error('Error preparing workflow:', error);
      toast.error('Error preparing workflow data');
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        id={id}
        onClick={() => setIsSettingsModalOpen(true)}
        disabled={disabled || isSaving}
        className={className}
        {...props}
      >
        <Save className='mr-2 h-4 w-4' />
        {isSaving ? 'Saving...' : 'Save Workflow'}
      </Button>

      {/* Workflow Settings Dialog */}
      <WorkflowSettingsDialog
        isOpen={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        reEnrollment={reEnrollment}
        setReEnrollment={setReEnrollment}
        enrollExisting={enrollExisting}
        setEnrollExisting={setEnrollExisting}
        removeIfIneligible={removeIfIneligible}
        setRemoveIfIneligible={setRemoveIfIneligible}
        onSave={handleSaveWorkflow}
        mode='create'
      />
    </>
  );
};

export default SaveWorkflow;
